import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { VentaListItem } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

export function VentasList() {
  const { data, loading, error } = useSupabaseQuery<VentaListItem[]>(async () => {
    const { data, error } = await supabase
      .from('ventas')
      .select('id, fecha, total, estado, clientes ( nombre, apellido )')
      .order('fecha', { ascending: false })
      .limit(50);

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => {
      const cliente = row.clientes as { nombre: string; apellido: string | null } | null;
      return {
        id: row.id as string,
        fecha: row.fecha as string,
        total: row.total as number,
        estado: row.estado as 'confirmada' | 'anulada',
        cliente_nombre: cliente ? `${cliente.nombre} ${cliente.apellido ?? ''}`.trim() : 'Sin clienta',
      };
    });

    return { data: items, error: null };
  });

  return (
    <div>
      <PageHeader
        title="Ventas"
        description="Últimas 50 ventas registradas"
        actions={
          <Link to="/admin/ventas/nueva" className="btn-primary">
            Nueva venta
          </Link>
        }
      />

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState title="No hay ventas registradas" description="Crea la primera desde el botón de arriba." />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Clienta</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {data.map((v) => (
                <tr key={v.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx/60">
                    {new Date(v.fecha).toLocaleString('es-CO', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3 font-medium text-onyx">{v.cliente_nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{COP.format(v.total)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`badge ${
                        v.estado === 'confirmada' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {v.estado}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
